import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { AssetLoader } from "@nolimitcity/slot-game/bin/asset/AssetLoader";
import { FlowState } from "@nolimitcity/slot-game/bin/flowcontrol/FlowState";
import { GameFlowController } from "@nolimitcity/slot-game/bin/flowcontrol/GameFlowController";
import { NolimitApplication } from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import { NolimitPlugin } from "@nolimitcity/slot-launcher/bin/NolimitPlugin";
import { LogLevel } from "@nolimitcity/slot-launcher/bin/utils/Logger";

import { EventsManager, GameEvents } from "../abstracts/events/EventsManager";
import {
    AbstractFlowPresentation,
    setModelForFlowPresentation
} from "../abstracts/flow/AbstractFlowStatePresentation";
import { AbstractGameMode } from "../abstracts/flow/AbstractGameMode";
import {
    AbstractModuleController,
    setModelForModulesController
} from "../abstracts/modules/AbstractModuleController";
import { initializeGameLogger } from "../abstracts/utils/AbstractConsole";
import { AbstractTimelineController } from "../abstracts/utils/AbstractTimelineController";
import { playSound } from "../abstracts/utils/SoundUtils";
import { DDDAssetsLoader } from "./assetsLoader/DDDAssetsLoader";
import { DDDSoundNames } from "./assetsLoader/DDDAssetsNames";
import { devDebugConfig } from "./config/DDDConfig";
import { DDDFreespinBetPresentation } from "./flow/freespin/DDDFreespinBetPresentation";
import { DDDFreespinIdlePresentation } from "./flow/freespin/DDDFreespinIdlePresentation";
import { DDDFreespinResultPresentation } from "./flow/freespin/DDDFreespinResultPresentation";
import { DDDFreespinWinPresentation } from "./flow/freespin/DDDFreespinWinPresentation";
import { DDDNormalBetPresentation } from "./flow/normal/DDDNormalBetPresentation";
import { DDDNormalIdlePresentation } from "./flow/normal/DDDNormalIdlePresentation";
import { DDDNormalResultPresentation } from "./flow/normal/DDDNormalResultPresentation";
import { DDDNormalWinPresentation } from "./flow/normal/DDDNormalWinPresentation";
import { DDDRespinBetPresentation } from "./flow/respin/DDDRespinBetPresentation";
import { DDDRespinIdlePresentation } from "./flow/respin/DDDRespinIdlePresentation";
import { DDDRespinResultPresentation } from "./flow/respin/DDDRespinResultPresentation";
import { DDDRespinWinPresentation } from "./flow/respin/DDDRespinWinPresentation";
import {
    DDDSuperFreespinBetPresentation
} from "./flow/superFreespin/DDDSuperFreespinBetPresentation";
import {
    DDDSuperFreespinIdlePresentation
} from "./flow/superFreespin/DDDSuperFreespinIdlePresentation";
import {
    DDDSuperFreespinResultPresentation
} from "./flow/superFreespin/DDDSuperFreespinResultPresentation";
import {
    DDDSuperFreespinWinPresentation
} from "./flow/superFreespin/DDDSuperFreespinWinPresentation";
import { DDDModel } from "./model/DDDModel";
import { DDDBackgroundController } from "./modules/Background/DDDBackgroundController";
import { DDDReelsController } from "./modules/Reels/DDDReelsController";
import { DDDSoundController } from "./modules/Sound/DDDSoundController";
import { DDDWinController } from "./modules/Winnings/DDDWinController";
import { DDDSoundDebugger } from "./utils/DDDSoundDebugger";
import { Speedster } from "./utils/Speedster";
import { SpinePreviewer } from "./utils/SpinePreviewer";
import {Skinner} from "@nolimitcity/slot-launcher/bin/utils/Skinner";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";

export class DDD extends SlotGame {
    public static model: DDDModel;
    public static assetLoader: AssetLoader;
    public static gameFlowController: GameFlowController;
    private skinner:Skinner;
    constructor() {
        super();
        const validSkins:string[] = [
            "sweep"
        ]
        this.skinner = new Skinner(validSkins);
    }

    private handleSweepInit(){
        //Making sure that skinID is set based  on "replaceWordFree" gcc.
        if (SlotGame.apiPlugin.gameClientConfiguration.isSet) {
            if (SlotGame.apiPlugin.gameClientConfiguration.sweepGfx) {
                this.skinner.skinId = "sweep";
            }
        } else {
            SlotGame.apiPlugin.events.on(APIEvent.GAME_CLIENT_CONFIGURATION_APPLIED, () => {
                if (SlotGame.apiPlugin.gameClientConfiguration.sweepGfx) {
                    this.skinner.skinId = "sweep";
                }
            })
        }
    }

    private handleSweepGetReady() : Promise<any>{
        if (this.skinner.skinId != undefined) {
            const replaceMap:Map<string, string> = new Map();
            //Bonus
            replaceMap.set("BOOSTED_BET", "BOOSTED_BET.png");
            replaceMap.set("BOOSTED_BET_1", "BOOSTED_BET_1.png");
            replaceMap.set("FREESPIN_8_SPINS", "FREESPIN_8_SPINS.png");
            return this.skinner.loadSkin(replaceMap);
        }
        return Promise.resolve();
    }
    private registerModules(): void {
        let layerIndex: number = 0;

        const addController = (controller: AbstractModuleController) => {
            if (controller.view.isDisabled) {
                return;
            }

            NolimitApplication.addLayerAt(
                controller.name.replace(`Controller`, ``) + `-layer`,
                controller.view,
                layerIndex
            );
            layerIndex++;
        };

        //add them in the order you need them displayed
        NolimitApplication.addLayer(`Speedster`, new Speedster());
        NolimitApplication.addLayerAt(`Spine Viewer`, new SpinePreviewer(DDD.assetLoader));


        // const fpsCounter = new PixiFps();
        // NolimitApplication.addLayer(`Speedster`, fpsCounter);

        // stage.addChild();
        addController(new DDDBackgroundController());
        addController(new DDDReelsController());
        addController(new DDDWinController());
        addController(new DDDSoundController());
    }

    public init(): Promise<NolimitPlugin> {
        return super.init().then((value: NolimitPlugin) => {
            DDD.model = new DDDModel(SlotGame.apiPlugin, true);

            setModelForModulesController(DDD.model);
            setModelForFlowPresentation(DDD.model);
            initializeGameLogger(`DDD`);

            DDD.gameFlowController = new GameFlowController(SlotGame.apiPlugin, DDD.model);
            DDD.gameFlowController.flowStateLogger.level = LogLevel.DEV;

            //IDLE
            const normalIdle = new DDDNormalIdlePresentation();
            const respinIdle = new DDDRespinIdlePresentation();
            const freespinIdle = new DDDFreespinIdlePresentation();
            const superFreespinIdle = new DDDSuperFreespinIdlePresentation();

            //BET
            const normalBet = new DDDNormalBetPresentation();
            const respinBet = new DDDRespinBetPresentation();
            const freespinBet = new DDDFreespinBetPresentation();
            const superFreespinBet = new DDDSuperFreespinBetPresentation();

            //RESULT
            const normalResult = new DDDNormalResultPresentation();
            const respinResult = new DDDRespinResultPresentation();
            const freespinResult = new DDDFreespinResultPresentation();
            const superFreespinResult = new DDDSuperFreespinResultPresentation();

            //WIN
            const normalWin = new DDDNormalWinPresentation();
            const respinWin = new DDDRespinWinPresentation();
            const freespinWin = new DDDFreespinWinPresentation();
            const superFreespinWin = new DDDSuperFreespinWinPresentation();

            DDD.gameFlowController.addGameModeFlowStatePresentationMap(
                AbstractGameMode.NORMAL,
                new Map<FlowState, AbstractFlowPresentation>([
                    [FlowState.IDLE, normalIdle],
                    [FlowState.BET, normalBet],
                    [FlowState.RESULT, normalResult],
                    [FlowState.WIN, normalWin]
                ])
            );

            DDD.gameFlowController.addGameModeFlowStatePresentationMap(
                AbstractGameMode.RESPIN,
                new Map<FlowState, AbstractFlowPresentation>([
                    [FlowState.IDLE, respinIdle],
                    [FlowState.BET, respinBet],
                    [FlowState.RESULT, respinResult],
                    [FlowState.WIN, respinWin]
                ])
            );

            DDD.gameFlowController.addGameModeFlowStatePresentationMap(
                AbstractGameMode.FREESPIN,
                new Map<FlowState, AbstractFlowPresentation>([
                    [FlowState.IDLE, freespinIdle],
                    [FlowState.BET, freespinBet],
                    [FlowState.RESULT, freespinResult],
                    [FlowState.WIN, freespinWin]
                ])
            );


            DDD.gameFlowController.addGameModeFlowStatePresentationMap(
                AbstractGameMode.SUPER_FREESPIN,
                new Map<FlowState, AbstractFlowPresentation>([
                    [FlowState.IDLE, superFreespinIdle],
                    [FlowState.BET, superFreespinBet],
                    [FlowState.RESULT, superFreespinResult],
                    [FlowState.WIN, superFreespinWin]
                ])
            );

            this.handleSweepInit();
            //xbet

            return value;
        });
    }

    public getReady(): Promise<NolimitPlugin> {
        //Show intro and load game AssetsModule
        return super.getReady().then((value: NolimitPlugin) => {
            // const fontLoader = new FontLoader(NolimitApplication.resourcePath);
            // fontLoader.add(OpenSans.ITALIC_600);
            // fontLoader.add(OpenSans.NORMAL_300);
            // fontLoader.add(OpenSans.NORMAL_400);
            // fontLoader.add(OpenSans.NORMAL_700);
            // fontLoader.add(DDDWaysTTF.NORMAL);
            //
            // AssetsConfig.addToAssetsConfig(DDDAssetsLoader.RESOURCES_GROUPS);
            // DDD.assetLoader = new AssetLoader(
            //     DDD.apiPlugin,
            //     NolimitApplication.resourcePath + `/resources/`
            // );
            // setAssetsLoader(DDD.assetLoader);

            return Promise.all([DDDAssetsLoader.loadTTF(), this.handleSweepGetReady()]).then(() => {
                this.registerModules();

                devDebugConfig.showTimelineController && new AbstractTimelineController();
                devDebugConfig.showSoundDebugger && new DDDSoundDebugger();

                DDD.apiPlugin.freeBets.events.on(DDD.apiPlugin.freeBets.FREE_BETS_START, () => {
                    EventsManager.trigger(GameEvents.ON_FREE_BETS_START);

                    DDD.apiPlugin.freeBets.events.once(DDD.apiPlugin.freeBets.FREE_BETS_END, () => {
                        EventsManager.trigger(GameEvents.ON_FREE_BETS_END);
                    });
                });

                return value;
            });
        });
    }

    public getReadyToStart(): Promise<NolimitPlugin> {
        //Game assets loaded, waiting for user to start game.
        (<any>NolimitApplication.pixiApp.renderer.mask).enableScissor = true;
        return super.getReadyToStart().then((value: NolimitPlugin) => {
            return value;
        });
    }

    public start(): Promise<NolimitPlugin> {
        playSound({ name: DDDSoundNames.CONTINUE_BUTTON });
        //User has pressed continue and the game starts.
        return super.start().then((value: NolimitPlugin) => {
            DDD.gameFlowController.start();

            return value;
        });
    }

    private paytable:string = require("../../nolimit/screens/paytable.mustache");
    public getPaytable(): Promise<string> {
        return new Promise((resolve, reject) => {
            const data = {
                skinPath: "screens/images/"
            };
            if (this.skinner.skinId != undefined) {
                data.skinPath = `../resources/images/${this.skinner.skinId}/`;
            }
            let paytableString: string = (<any>this.paytable).text;
            paytableString = paytableString.replace(/{{skinPath}}/g, data.skinPath);
            resolve(paytableString);
        });
    }
    public getNoWinGameData(): any {
        return {
            accumulatedRoundWin: 0,
            betWayWins: [],
            boostedBet: false,
            brokeTheBank: false,
            evaluatedArea: [
                [`L2`, `M2`, `L2`],
                [`M3`, `M1`, `M5`],
                [`L3`, `L3`, `M5`],
                [`L2`, `L4`, `L2`],
                [`M2`, `L5`, `L3`]
            ],
            freespinsLeft: 0,
            freespinsPlayed: 0,
            fsRoundWin: 0,
            mode: `NORMAL`,
            nextMode: `NORMAL`,
            payOutMultiplier: 1,
            playedBetValue: 1,
            reels: [
                [`M2`, `L2`, `M2`],
                [`M5`, `M3`, `M1`],
                [`M2`, `L3`, `L3`],
                [`M5`, `L2`, `S`],
                [`M3`, `L5`, `M2`]
            ],
            serverTestInfo: {
                OverrideReels:
                    [
                        [`M2`, `L2`, `M2`, null],
                        [`M5`, `M3`, `M1`, null],
                        [`M2`, `L3`, `L3`, null],
                        [`M5`, `L2`, `S`, null],
                        [`M3`, `L5`, `M2`, null]
                    ]
            },
            stickyWildMultipliers: [
                [-1, -1, -1],
                [-1, -1, -1],
                [-1, -1, -1],
                [-1, -1, -1],
                [-1, -1, -1]

            ],
            stickyXNudge: [false, false, false, false, false],
            symbolSizes: [
                [1, 1, 1],
                [1, 1, 1],
                [1, 1, 1],
                [1, 1, 1],
                [1, 1, 1]
            ],
            wasFeatureBuy: false,
            win: 0,
            xNudgeMultipliersCurrent: [-1, -1, -1, -1, -1],
            xNudgeMultipliersNext: [-1, -1, -1, -1, -1]
        };
    }
}
